import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../lib/api';
import { setAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      const { access_token, user } = response.data;
      
      if (user.role !== 'admin') {
        setError('Admin access required');
        setLoading(false);
        return;
      }

      setAuth(access_token, user);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="admin-login-page">
      <div className="login-form-side bg-white">
        <div className="login-box">
          <div className="mb-12">
            <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              AI Accountability Standards
            </h1>
            <p className="text-zinc-500 mt-2">Administrator Portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="alert alert-error flex items-center gap-2" data-testid="login-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="form-field">
              <Label htmlFor="email" className="form-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input rounded-none"
                placeholder="admin@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-field">
              <Label htmlFor="password" className="form-label">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input rounded-none"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary rounded-none h-11"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-xs text-zinc-400 mt-12 text-center">
            Accountability declaration and assessment platform for AI governance standards.
          </p>
        </div>
      </div>
      <div className="login-image-side bg-zinc-900 flex items-center justify-center">
        <div className="text-center p-12">
          <div className="text-white/20 text-9xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            AIAS
          </div>
          <p className="text-white/40 mt-4 text-sm tracking-widest uppercase">
            Accountability Council
          </p>
        </div>
      </div>
    </div>
  );
}
